import React, { useCallback, useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  MessageSquare,
  Eye,
  Copy,
  FileText,
  Clock,
  User,
  Stethoscope,
  Sparkles,
  Upload,
  Paperclip,
  ChevronDown,
  GripVertical,
  Download,
  Search,
  Users,
  Star,
  Globe,
  Shield,
  Calendar,
  ClipboardList,
  X,
  Mail,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import useTitanApp from "../../hooks/useTitanApp";

import {
  ConsultationQuestion,
  ConsultationSection,
  ConsultationTemplate,
  ReviewData,
  useConsultationsData,
  ConsultationAction,
  TreatmentPlanAppointment,
  TreatmentPlanTask,
  validateTemplateSections,
} from "@/lib/settings/ConsultationsData";
import {
  setShowPermissionMessage,
  setShowPermissionModal,
} from "@/dispatchers/appDispatchers";
import ConsultationSettingsSkeleton from "./skeletons/ConsultationSettingsSkeleton";
import { useFieldArray, useForm } from "react-hook-form";
import { CharLimitMessage } from "@/components/common/CharLimitMessage";
import { CONSULTATION_TEMPLATE_LIMITS } from "@/lib/settings/templateLimits";
import { deleteConfirmationMessage } from "@/data/constantStrings";
import LinkedDocumentSelector from "./LinkedDocumentSelector";
import { ReviewTemplateModal } from "./ReviewTemplateModal";
import { useToast } from "@/hooks/use-toast";
import { useDocumentsData } from "../Documents/documentsData";
import { trackFirstTimeIntercomEvent } from "@/utils/trackFirstTimeIntercomEvent";
import PermissionRequiredModal from "../PermissionRequiredModal";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import { accessTitanverseTemplateUpsert } from "@/data/constantStrings";
import {
  clearDefaultCalenderAppointmentPharmacyServices,
  clearServiceUsersByCompany,
  getDefaultServicesByCompanyIdAndPharmacyId,
} from "@/dispatchers/pharmacyServiceDispatchers";
import {
  getRoomsByServiceId,
  getPreConsultTemplateByCompanyId,
} from "@/dispatchers/templateDispatchers";

interface ConsultationSettingsTabProps {
  isDarkMode?: boolean;
  onAddTemplate: () => void;
  onEditTemplate: (template: ConsultationTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  onDuplicateTemplate: (template: ConsultationTemplate) => void;
}

interface ConsultationFormPanelProps {
  template?: ConsultationTemplate;
  isDarkMode?: boolean;
  onSave: (templateData: Partial<ConsultationTemplate>) => void;
  onBack: () => void;
  confirmDelete: (onConfirm: () => void, itemType?: string) => void;
}

interface ConsultationPreviewModalProps {
  template: ConsultationTemplate;
  isDarkMode?: boolean;
  onClose: () => void;
}


function SortableActionItem({
  action,
  index,
  isDarkMode,
  editingActionId,
  editingActionLabel,
  setEditingActionLabel,
  saveEditAction,
  cancelEditAction,
  startEditAction,
  removeAction,
}: {
  action: ConsultationAction;
  index: number;
  isDarkMode: boolean;
  editingActionId: string | null;
  editingActionLabel: string;
  setEditingActionLabel: (v: string) => void;
  saveEditAction: () => void;
  cancelEditAction: () => void;
  startEditAction: (action: ConsultationAction) => void;
  removeAction: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: action.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-colors duration-200 ${
        isDarkMode
          ? "border-gray-600 bg-gray-700/50 hover:bg-gray-700"
          : "border-gray-200 bg-gray-50 hover:bg-gray-100"
      }`}
    >
      <button
        type="button"
        className={`cursor-grab active:cursor-grabbing flex-shrink-0 touch-none ${
          isDarkMode
            ? "text-gray-500 hover:text-gray-300"
            : "text-gray-400 hover:text-gray-600"
        }`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <span
        className={`text-xs font-medium w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
          isDarkMode ? "bg-gray-600 text-gray-300" : "bg-gray-200 text-gray-600"
        }`}
      >
        {index + 1}
      </span>

      {editingActionId === action.id ? (
        <div className="flex items-center flex-1 gap-2">
          <Input
            value={editingActionLabel}
            onChange={(e) => setEditingActionLabel(e.target.value)}
            maxLength={CONSULTATION_TEMPLATE_LIMITS.actionLabel}
            className={`flex-1 h-7 text-sm ${
              isDarkMode ? "bg-gray-600 border-gray-500 text-white" : ""
            }`}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                saveEditAction();
              }
              if (e.key === "Escape") cancelEditAction();
            }}
            autoFocus
          />
          <Button
            onClick={saveEditAction}
            size="sm"
            variant="ghost"
            className="px-2 text-green-600 h-7 hover:text-green-700 hover:bg-green-50"
          >
            <Save className="w-3.5 h-3.5" />
          </Button>
          <Button
            onClick={cancelEditAction}
            size="sm"
            variant="ghost"
            className="px-2 text-gray-400 h-7 hover:text-gray-600"
          >
            <X className="w-3.5 h-3.5" />
          </Button>
          <CharLimitMessage
            value={editingActionLabel}
            max={CONSULTATION_TEMPLATE_LIMITS.actionLabel}
            className="mt-0 text-xs whitespace-nowrap"
          />
        </div>
      ) : (
        <>
          <span
            className={`flex-1 text-sm font-medium transition-colors duration-300 ${
              isDarkMode ? "text-gray-200" : "text-gray-800"
            }`}
          >
            {action.label}
          </span>
          <div className="flex items-center gap-1">
            <Button
              onClick={() => startEditAction(action)}
              size="sm"
              variant="ghost"
              className={`h-7 w-7 p-0 ${
                isDarkMode
                  ? "text-gray-400 hover:text-white hover:bg-gray-600"
                  : "text-gray-400 hover:text-gray-700 hover:bg-gray-200"
              }`}
            >
              <Edit2 className="w-3.5 h-3.5" />
            </Button>
            <Button
              onClick={() => removeAction(action.id)}
              size="sm"
              variant="ghost"
              className="p-0 text-red-400 h-7 w-7 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function ConsultationSettingsTab({
  onAddTemplate,
  onEditTemplate,
  onDeleteTemplate,
  onDuplicateTemplate,
}: ConsultationSettingsTabProps) {
  const [templates, setTemplates] = useState<ConsultationTemplate[]>(
    // defaultConsultationTemplates
    [],
  );

  const {
    initialTemplates,
    isDropdownAiTemplatesLoading,
    setGeneratedTemplates,
    generatedTemplates,
    darkMode: isDarkMode,
  } = useConsultationsData();

  const handleAddTemplate = () => {
    setGeneratedTemplates(null); // Clear previous generated template
    if (generatedTemplates === null) {
      onAddTemplate();
    }
    // Open Add Template screen/modal
  };
  useEffect(() => {
    setTemplates(initialTemplates);
  }, [initialTemplates]);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<ConsultationTemplate | null>(null);

  const filteredUserTemplates = templates
    .filter((template) => {
      const matchesSearch =
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || template.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const canUpsertTemplate = checkUserPermissions(
    accessTitanverseTemplateUpsert,
  );

  const getCategoryBadgeColor = (category: string) => {
    switch (category) {
      case "general":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "specialty":
        return "bg-purple-100 text-purple-800 border-purple-200";
      case "follow-up":
        return "bg-green-100 text-green-800 border-green-200";
      case "emergency":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const handlePreview = (template: ConsultationTemplate) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

// Add this helper to get the minimum duration value based on frequency
const getMinDurationValue = (frequency: string): number => {
  switch (frequency) {
    case 'Weekly':
      return 1;
    case 'Monthly':
      return 1;
    case 'Yearly':
      return 1;
    default:
      return 1;
  }
};

  if (isDropdownAiTemplatesLoading) {
    return <ConsultationSettingsSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            Consultation Templates
          </h2>
          <p
            className={`text-sm mt-1 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            Manage your templates and discover community-created ones
          </p>
        </div>
        <Button
          onClick={() => {
            if (!canUpsertTemplate) {
              setShowPermissionMessage("Create or update template");
              setShowPermissionModal(true);
              return;
            }

            handleAddTemplate();
          }}
          className={`bg-slate-900 hover:bg-slate-800 ${
            !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
          }`}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Template
        </Button>
      </div>

      {/* Tabs */}
      <div
        className={`border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        }`}
      >
        <nav className="flex -mb-px space-x-8">
          <button
            // onClick={() => setActiveTab("my-templates")}
            className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-300 my-templates ${
              isDarkMode
                ? "border-white text-white hover:text-gray-300 hover:border-gray-300"
                : "border-slate-900 text-slate-900 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              My Templates ({filteredUserTemplates.length})
            </div>
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute w-4 h-4 text-gray-400 transform -translate-y-1/2 left-3 top-1/2" />
            <Input
              placeholder={"Search your templates..."}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
              }}
              className="pl-10"
            />
          </div>
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger
            className={`w-48 transition-colors duration-300 ${
              isDarkMode ? "bg-gray-800 border-gray-600 text-white" : "bg-white"
            }`}
          >
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="general">General</SelectItem>
            {/* <SelectItem value="specialty">Specialty</SelectItem>
            <SelectItem value="follow-up">Follow-up</SelectItem>
            <SelectItem value="emergency">Emergency</SelectItem> */}
          </SelectContent>
        </Select>
      </div>

      {/* Templates Grid */}
      <div className="space-y-3">
        {filteredUserTemplates.map((template) => (
          <div
            key={template.id}
            className={`border rounded-lg p-4 transition-all duration-300 ${
              isDarkMode
                ? "border-gray-700 bg-gray-800 hover:bg-gray-750"
                : "border-gray-200 hover:shadow-md"
            }`}
          >
            <div className="flex items-center justify-between">
              {/* Left Section - Template Info */}
              <div className="flex items-center flex-1 gap-4">
                {/* Icon and Name */}
                <div className="flex items-center gap-3">
                  <Shield className="flex-shrink-0 w-5 h-5 text-blue-500" />
                  <div>
                    <div className="flex items-center gap-2">
                      <h3
                        className={`font-semibold transition-colors duration-300 ${
                          isDarkMode ? "text-white" : "text-gray-900"
                        }`}
                      >
                        {template?.name}
                      </h3>
                      {template?.isDefault && (
                        <Badge variant="secondary" className="text-xs">
                          Default
                        </Badge>
                      )}
                    </div>
                    <p
                      className={`text-sm mt-1 line-clamp-1 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      {template?.description}
                    </p>
                  </div>
                </div>

                {/* Category Badge */}
                <Badge
                  className={`text-xs ${getCategoryBadgeColor(
                    template?.category,
                  )} ${isDarkMode ? "border-gray-600" : ""}`}
                  variant="outline"
                >
                  {template?.category.charAt(0).toUpperCase() +
                    template?.category.slice(1)}
                </Badge>

                {/* Template Stats */}
                <div className="flex items-center gap-6 ml-auto text-xs">
                  <div
                    className={`flex items-center gap-1 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    {/* <User className="w-3 h-3" />
                    {template?.usageCount} uses */}
                  </div>
                  <div
                    className={`flex items-center gap-1 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-500"
                    }`}
                  >
                    <Calendar className="w-3 h-3" />
                    {template?.lastReviewedAt
                      ? `Reviewed ${new Date(template.lastReviewedAt).toLocaleDateString()}`
                      : "Not reviewed"}
                  </div>
                </div>
              </div>

              {/* Right Section - Actions */}
              <div className="flex items-center gap-2">
                {/* EDIT */}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (!canUpsertTemplate) {
                      setShowPermissionMessage("Create or update template");
                      setShowPermissionModal(true);
                      return;
                    }

                    onEditTemplate(template);
                  }}
                  className={`${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                  } ${!canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>

                {/* DELETE */}
                {!template?.isDefault && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (!canUpsertTemplate) {
                        setShowPermissionMessage("Create or update template");
                        setShowPermissionModal(true);
                        return;
                      }

                      onDeleteTemplate(template?.id);
                    }}
                    className={`text-red-500 hover:text-red-700 hover:bg-red-50 ${
                      !canUpsertTemplate ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Empty States */}
      {filteredUserTemplates.length === 0 && (
        <div className="py-12 text-center">
          <Shield
            className={`w-12 h-12 mx-auto mb-4 transition-colors duration-300 ${
              isDarkMode ? "text-gray-600" : "text-gray-400"
            }`}
          />
          <h3
            className={`text-lg font-medium mb-2 transition-colors duration-300 ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          >
            No templates found
          </h3>
          <p
            className={`text-sm transition-colors duration-300 ${
              isDarkMode ? "text-gray-500" : "text-gray-500"
            }`}
          >
            {searchTerm || selectedCategory !== "all"
              ? "Try adjusting your search or filter criteria"
              : "Create your first consultation template to get started"}
          </p>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <ConsultationPreviewModal
          template={previewTemplate}
          isDarkMode={isDarkMode}
          onClose={() => {
            setShowPreview(false);
            setPreviewTemplate(null);
          }}
        />
      )}
    </div>
  );
}

export function ConsultationFormPanel({
  template,
  onSave,
  onBack,
  confirmDelete,
}: ConsultationFormPanelProps) {
  const { darkMode: isDarkMode, currentUser } = useConsultationsData();
  const [templatePrompt, setTemplatePrompt] = useState("");
  const [uploadedDocument, setUploadedDocument] = useState<File | null>(null);
  const [documentContent, setDocumentContent] = useState("");
  const [showBuilder, setShowBuilder] = useState(!!template);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamedSummary, setStreamedSummary] = useState("");
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [fileError, setFileError] = useState<string>("");

  const [detailsSubTab, setDetailsSubTab] = useState<
    "notes" | "questions" | "actions" | "treatment"
  >("notes");

  const [newActionLabel, setNewActionLabel] = useState("");
  const [editingActionId, setEditingActionId] = useState<string | null>(null);
  const [editingActionLabel, setEditingActionLabel] = useState("");

  const [addingTaskForAppt, setAddingTaskForAppt] = useState<string | null>(null);
  const [newTaskText, setNewTaskText] = useState("");
  const [collapsedAppointments, setCollapsedAppointments] = useState<Set<string>>(
    () => new Set(),
  );
  const [invalidAppointmentIds, setInvalidAppointmentIds] = useState<Set<string>>(
    () => new Set(),
  );
  // Inline errors for the Questions builder, keyed by position:
  // `title-${si}`, `questions-${si}`, `question-${si}-${qi}`.
  const [sectionFieldErrors, setSectionFieldErrors] = useState<
    Record<string, string>
  >({});
  const clearSectionFieldError = (key: string) =>
    setSectionFieldErrors((prev) => {
      if (!(key in prev)) return prev;
      const { [key]: _removed, ...rest } = prev;
      return rest;
    });

  const { templateState, pharmacyServiceState, appState } = useTitanApp();
  const { consultationStreamedSummary, currentPreConsultTemplates } = templateState || {};
  const { defaultCalenderAppointmentPharmacyServices, isPharmacyServiceLoading } = pharmacyServiceState || {};
  const { currentPharmacyId, companyId } = appState || {};

  // Per-serviceId rooms cache — avoids the single global Redux array overwriting itself
  const [roomsByServiceId, setRoomsByServiceId] = useState<Record<string, any[]>>({});
  const roomsCacheRef = React.useRef<Record<string, any[]>>({});
  const [loadingRoomsServiceIds, setLoadingRoomsServiceIds] = useState<Set<string>>(() => new Set());

  const fetchRoomsForService = useCallback(async (serviceId: string) => {
    if (!serviceId || !companyId) return;
    setLoadingRoomsServiceIds((prev) => new Set(prev).add(serviceId));
    try {
      const rooms = await getRoomsByServiceId({ serviceId, companyId });
      roomsCacheRef.current = { ...roomsCacheRef.current, [serviceId]: Array.isArray(rooms) ? rooms : [] };
      setRoomsByServiceId({ ...roomsCacheRef.current });
    } finally {
      setLoadingRoomsServiceIds((prev) => {
        const next = new Set(prev);
        next.delete(serviceId);
        return next;
      });
    }
  }, [companyId]);
  const {
    generatedTemplates,
    generateConsultationTemplate,
    getServiceSummaryStream,
    handleTemplateSubmit,
    isAiTemplateLoading,
    isSaveAiTemplateLoading,
    isConsultationLoading,
    isPatientConsultaionCountLoading,
    buttonDisabled,
    setIsConsultationSummaryLoading,
    setGeneratedTemplates,
    serviceSummaryLoading,
    hasRequiredHeading,
  } = useConsultationsData();
  const { documentSizeLimitInMbs } = useDocumentsData() || {};
  const { toast } = useToast();

  const handleReviewSubmit = useCallback(
    (reviewData: ReviewData) => {
      setFormData((prev) => {
        if (!prev) return prev;

        return {
          ...prev,
          lastReviewedAt: reviewData.timestamp,
          reviewNote: reviewData.reviewNotes,
        };
      });

      setShowReviewModal(false);

      toast({
        title: "Review Submitted Successfully",
        description:
          "The template has been reviewed and approved for clinical use.",
      });
    },
    [toast],
  );

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setFileError(""); // Clear previous errors

    if (!file) {
      setUploadedDocument(null);
      setDocumentContent("");
      return;
    }

    // Validate file size
    const maxSizeInBytes = (documentSizeLimitInMbs || 5) * 1024 * 1024; // Default to 5MB if not specified
    if (file.size > maxSizeInBytes) {
      const maxSizeMB = documentSizeLimitInMbs || 5;
      setFileError(
        `File size exceeds the limit of ${maxSizeMB}MB. Please choose a smaller file.`,
      );
      setUploadedDocument(null);
      setDocumentContent("");

      // Clear the file input
      event.target.value = "";

      // Show error toast
      toast({
        title: "File Too Large",
        description: `Maximum file size is ${maxSizeMB}MB. Please select a smaller file.`,
        variant: "destructive",
      });
      return;
    }

    // Validate file type
    const allowedTypes = [".txt", ".pdf", ".doc", ".docx"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      setFileError(
        `File type not allowed. Please upload only: ${allowedTypes.join(", ")} files.`,
      );
      setUploadedDocument(null);
      setDocumentContent("");

      // Clear the file input
      event.target.value = "";

      // Show error toast
      toast({
        title: "Invalid File Type",
        description: `Please upload only ${allowedTypes.join(", ")} files.`,
        variant: "destructive",
      });
      return;
    }

    setUploadedDocument(file);

    // Read file content
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setDocumentContent(content);
    };

    reader.onerror = () => {
      setFileError("Error reading file. Please try again.");
      setUploadedDocument(null);
      setDocumentContent("");

      toast({
        title: "File Read Error",
        description: "Unable to read the file. Please try again.",
        variant: "destructive",
      });
    };

    reader.readAsText(file);
  };

  const [formData, setFormData] = useState<ConsultationTemplate>(() => {
    if (template) {
      // Expand all sections by default for existing templates
      setExpandedSections(new Set(template.sections.map((s) => s.id)));
      return template;
    }
    return {
      id: "",
      name: "",
      description: "",
      category: "general" as const,
      companyID: "",
      consultationSummary: "",
      isDefault: false,
      isActive: true,
      sections: [],
      createdAt: "",
      createdBy: "",
      createdByName: "",
      updatedAt: "",
      updatedBy: "",
      updatedByName: "",
      archiveAt: "",
      archiveBy: "",
      archiveByName: "",
      usageCount: 0,
      freeTextContent: "",
      tempPrompt: "",
      linkedDocumentId: "",
      forceUserSignOff: false,
      lastReviewedAt: "",
      reviewNote: "",
      consultationActions: [],
      treatmentPlan: { appointments: [] },
    };
  });

  useEffect(() => {
    if (template) {
      // Editing existing template - this takes priority
      setFormData({
        ...template,
      });
      setShowBuilder(true);
      setExpandedSections(new Set(template.sections.map((s) => s.id)));
    } else if (generatedTemplates) {
      setFormData({
        ...generatedTemplates,
      });
      reset(
        {
          ...generatedTemplates,
          tempPrompt: templatePrompt || "",
        },
        {
          keepErrors: true,
          keepDirty: false,
        },
      );
      setShowBuilder(true);
    }
  }, [template, generatedTemplates]);
  const updateField = (field: keyof ConsultationTemplate, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const addSection = () => {
    const newSectionId = `s${Date.now()}`;
    const newSection: ConsultationSection = {
      id: newSectionId,
      title: "",
      questions: [],
    };

    setFormData((prev) => {
      const updatedSections = [...prev.sections, newSection];
      setValue("sections", updatedSections, {
        shouldValidate: true,
        shouldDirty: true,
      });
      return {
        ...prev,
        sections: updatedSections,
      };
    });
    setExpandedSections((prev) => new Set([...prev, newSectionId]));
  };

  const updateSection = (
    index: number,
    field: keyof ConsultationSection,
    value: any,
  ) => {
    if (field === "title") clearSectionFieldError(`title-${index}`);
    setFormData((prev) => {
      const updatedSections = prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section,
      );
      setValue("sections", updatedSections, {
        shouldValidate: true,
        shouldDirty: true,
      });

      return {
        ...prev,
        sections: updatedSections,
      };
    });
  };

  const removeSection = (index: number) => {
    const sectionToRemove = formData.sections[index];
    const updatedSections = formData.sections.filter((_, i) => i !== index);
    // Positions shift, so position-keyed errors no longer apply.
    setSectionFieldErrors({});
    setFormData((prev) => ({
      ...prev,
      sections: updatedSections,
    }));
    setValue("sections", updatedSections, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      newSet.delete(sectionToRemove.id);
      return newSet;
    });
  };

  const reorderSections = (startIndex: number, endIndex: number) => {
    const reorderedSections = arrayMove(
      formData.sections,
      startIndex,
      endIndex,
    );
    setSectionFieldErrors({});
    setFormData((prev) => ({
      ...prev,
      sections: reorderedSections,
    }));
    setValue("sections", reorderedSections, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const reorderQuestions = (
    sectionIndex: number,
    startIndex: number,
    endIndex: number,
  ) => {
    setSectionFieldErrors({});
    const updatedSections = formData.sections.map((section, i) => {
      if (i !== sectionIndex) return section;
      return {
        ...section,
        questions: arrayMove(section.questions, startIndex, endIndex),
      };
    });

    setFormData((prev) => ({
      ...prev,
      sections: updatedSections,
    }));

    setValue(
      `sections.${sectionIndex}.questions`,
      updatedSections[sectionIndex].questions,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const addQuestion = (sectionIndex: number) => {
    const newQuestion: ConsultationQuestion = {
      id: `q${Date.now()}`,
      question: "",
      required: false,
      value: "",
    };
    clearSectionFieldError(`questions-${sectionIndex}`);
    const updatedSections = formData.sections.map((section, i) =>
      i === sectionIndex
        ? { ...section, questions: [...section.questions, newQuestion] }
        : section,
    );
    setFormData((prev) => ({
      ...prev,
      sections: updatedSections,
    }));
    setValue(
      `sections.${sectionIndex}.questions`,
      updatedSections[sectionIndex].questions,
      {
        shouldValidate: true,
        shouldDirty: true,
      },
    );
  };

  const updateQuestion = (
    sectionIndex: number,
    questionIndex: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => {
    if (field === "question") {
      clearSectionFieldError(`question-${sectionIndex}-${questionIndex}`);
    }
    setFormData((prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === sectionIndex
          ? {
              ...section,
              questions: section.questions.map((question, qi) =>
                qi === questionIndex
                  ? { ...question, [field]: value }
                  : question,
              ),
            }
          : section,
      ),
    }));
  };
  const removeQuestion = (sectionIndex: number, questionIndex: number) => {
    setSectionFieldErrors({});
    const updatedSections = formData.sections.map((section, i) =>
      i === sectionIndex
        ? {
            ...section,
            questions: section.questions.filter(
              (_, qi) => qi !== questionIndex,
            ),
          }
        : section,
    );

    setFormData((prev) => ({
      ...prev,
      sections: updatedSections,
    }));
    setValue(
      `sections.${sectionIndex}.questions`,
      updatedSections[sectionIndex].questions,
      { shouldValidate: true, shouldDirty: true },
    );
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Consultation Actions handlers
  const addAction = useCallback(() => {
    if (!newActionLabel.trim()) return;
    const newAction: ConsultationAction = {
      id: `action_${Date.now()}`,
      label: newActionLabel.trim(),
      enabled: false,
    };
    setFormData((prev) => ({
      ...prev,
      consultationActions: [...(prev.consultationActions || []), newAction],
    }));
    setNewActionLabel("");
  }, [newActionLabel]);

  const removeAction = useCallback((actionId: string) => {
    setFormData((prev) => ({
      ...prev,
      consultationActions: (prev.consultationActions || []).filter(
        (a) => a.id !== actionId,
      ),
    }));
  }, []);

  const updateAction = useCallback(
    (actionId: string, field: keyof ConsultationAction, value: any) => {
      setFormData((prev) => ({
        ...prev,
        consultationActions: (prev.consultationActions || []).map((a) =>
          a.id === actionId ? { ...a, [field]: value } : a,
        ),
      }));
    },
    [],
  );

  const startEditAction = useCallback((action: ConsultationAction) => {
    setEditingActionId(action.id);
    setEditingActionLabel(action.label);
  }, []);

  const saveEditAction = useCallback(() => {
    if (!editingActionId || !editingActionLabel.trim()) return;
    updateAction(editingActionId, "label", editingActionLabel.trim());
    setEditingActionId(null);
    setEditingActionLabel("");
  }, [editingActionId, editingActionLabel, updateAction]);

  const cancelEditAction = useCallback(() => {
    setEditingActionId(null);
    setEditingActionLabel("");
  }, []);

  const handleActionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setFormData((prev) => {
        const actions = prev.consultationActions || [];
        const oldIndex = actions.findIndex((a) => a.id === active.id);
        const newIndex = actions.findIndex((a) => a.id === over.id);
        return {
          ...prev,
          consultationActions: arrayMove(actions, oldIndex, newIndex),
        };
      });
    }
  }, []);

  const handleGenerateFromPrompt = async (prompt: string) => {
    if (!prompt.trim()) return;

    setIsStreaming(true);
    setIsConsultationSummaryLoading(true);
    const loadingTimeout = setTimeout(() => {
      setIsConsultationSummaryLoading(false);
    }, 20000);
    try {
      await generateConsultationTemplate(prompt, documentContent);
      await getServiceSummaryStream(
        prompt,
        setIsStreaming,
        (summary) => {
          setStreamedSummary(summary);
          updateField("consultationSummary", summary);
          setValue("consultationSummary", summary, {
            shouldValidate: true,
            shouldDirty: true,
          });
        },
        documentContent,
      );

      setShowBuilder(true);
    } catch (error) {
      console.error("Error during summary generation:", error);
    } finally {
      clearTimeout(loadingTimeout);
      setIsConsultationSummaryLoading(false);
      setIsStreaming(false);
    }
  };

  const TREATMENT_PLAN_CATEGORIES = [
  "Follow-up Consultation",
  "Progress Review",
  "Treatment Adjustment",
  "Final Assessment",
  "Medication Review",
  "Health Check",
];

const TREATMENT_PLAN_SERVICE_TYPES = [
  "General Consultation",
  "Follow-up Visit",
  "Vaccination",
  "Health Check",
  "Blood Test",
  "Specialist Consultation",
];

const TREATMENT_PLAN_DURATIONS = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "1 hour" },
  { value: "90", label: "1.5 hours" },
];

const TREATMENT_PLAN_ROOMS = [
  "Consultation Room 1",
  "Private Room A",
  "Treatment Room B",
  "Multi-Purpose Room",
];

const PRE_CONSULTATION_TEMPLATES = [
  "General Health Questionnaire",
  "Blood Pressure Pre-Consultation Form",
  "Weight Management Questionnaire",
  "Medication Review Checklist",
  "Vaccination Consent & Screening Form",
  "New Medicine Service (NMS) Questionnaire",
];

  const mappedServices = React.useMemo(() => {
    const services = defaultCalenderAppointmentPharmacyServices || [];
    return services
      .filter((s: any) => s.isActive && s.serviceType !== 1 && s.serviceType !== 3)
      .map((s: any) => ({
        _id: s._id,
        name: s.serviceName,
        serviceDuration: s.serviceDuration,
      }));
  }, [defaultCalenderAppointmentPharmacyServices]);

  const formatDurationLabel = (minutes: number) => {
    if (minutes < 60) return `${minutes} minutes`;
    const hours = minutes / 60;
    return hours % 1 === 0
      ? `${hours} hour${hours > 1 ? "s" : ""}`
      : `${hours} hours`;
  };

  const getDurationsForService = (serviceId: string, currentValue?: string) => {
    const svc = mappedServices.find((s) => s._id === serviceId);
    const base = parseInt(String(svc?.serviceDuration ?? 0), 10) || 0;
    const options =
      base <= 0
        ? TREATMENT_PLAN_DURATIONS
        : Array.from({ length: 5 }, (_, i) => base * (i + 1)).map((m) => ({
            value: String(m),
            label: formatDurationLabel(m),
          }));
    // Preserve a previously saved value even if it isn't one of the computed multiples
    if (currentValue && !options.some((o) => o.value === currentValue)) {
      const minutes = parseInt(currentValue, 10) || 0;
      return [
        { value: currentValue, label: formatDurationLabel(minutes) },
        ...options,
      ];
    }
    return options;
  };

  useEffect(() => {
    if (detailsSubTab === "treatment" && companyId && currentPharmacyId) {
      clearDefaultCalenderAppointmentPharmacyServices();
      clearServiceUsersByCompany();
      getDefaultServicesByCompanyIdAndPharmacyId({
        companyId,
        pharmacyId: currentPharmacyId,
        appointmentDate: new Date().toISOString().split("T")[0],
      });
      // Pre-fetch rooms for existing appointments so their room selects are pre-filled
      (formData.treatmentPlan?.appointments || []).forEach((appt) => {
        if (appt.serviceId) fetchRoomsForService(appt.serviceId);
      });
      // Pre-fetch pre-consult templates so the dropdown is populated on edit
      getPreConsultTemplateByCompanyId(companyId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailsSubTab, companyId, currentPharmacyId]);

  // Backfill the room display name for existing appointments once their service's
  // rooms load — the roomId alone doesn't carry the name, and if the user never
  // reopens the dropdown, `room` would otherwise stay blank and wipe out on save.
  useEffect(() => {
    setFormData((prev) => {
      const appointments = prev.treatmentPlan?.appointments || [];
      if (!appointments.length) return prev;
      let changed = false;
      const nextAppointments = appointments.map((a) => {
        if (!a.roomId) return a;
        const matched = (roomsByServiceId[a.serviceId] || []).find(
          (r: any) => r.roomId === a.roomId,
        );
        if (matched?.roomName && matched.roomName !== a.room) {
          changed = true;
          return { ...a, room: matched.roomName };
        }
        return a;
      });
      if (!changed) return prev;
      return {
        ...prev,
        treatmentPlan: { ...prev.treatmentPlan, appointments: nextAppointments },
      };
    });
  }, [roomsByServiceId]);

    // Treatment Plan handlers
  const toggleAppointmentCollapse = useCallback((id: string) => {
    setCollapsedAppointments((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const addTreatmentAppointment = useCallback(() => {
    const newId = `appt_${Date.now()}`;
    const newAppointment: TreatmentPlanAppointment = {
      id: newId,
      name: "",
      serviceId: "",
      category: "",
      duration: "",
      roomId: "",
      room: "",
      intervalValue: "",
      intervalUnit: "Weeks",
      isRecurring: false,
      recurrenceFrequency: "Monthly",
      recurrenceDurationValue: "",
      recurrenceDurationUnit: "Months",
      sendPreConsultationForm: false,
      preConsultationTemplateId: "",
      preConsultationSendValue: "24",
      preConsultationSendUnit: "Hours",
      tasks: [],
    };
    // Collapse existing appointments before adding the new one
    setCollapsedAppointments(
      new Set((formData.treatmentPlan?.appointments || []).map((a) => a.id)),
    );
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        appointments: [
          ...(prev.treatmentPlan?.appointments || []),
          newAppointment,
        ],
      },
    }));
  }, [formData.treatmentPlan?.appointments]);

    // Add this validation function before the component
const isValidRecurrenceCombination = (
  frequency: string, 
  durationUnit: string
): { isValid: boolean; message: string } => {
  const validCombinations: Record<string, string[]> = {
    'Weekly': ['Weeks', 'Months'],
    'Monthly': ['Months', 'Years'],
    'Yearly': ['Months', 'Years']
  };

  const allowedUnits = validCombinations[frequency] || [];
  
  if (!allowedUnits.includes(durationUnit)) {
    return {
      isValid: false,
      message: `${frequency} recurrence can only be set for ${allowedUnits.join(' or ')}. Please update the duration unit.`
    };
  }

  return { isValid: true, message: '' };
};

 const updateTreatmentAppointment = useCallback(
  (
    id: string,
    field: keyof Omit<TreatmentPlanAppointment, "tasks">,
    value: string | boolean,
  ) => {
    if (field === "name") {
      setInvalidAppointmentIds((prev) => {
        if (!prev.has(id)) return prev;
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }

    // If changing recurrence frequency or duration unit, validate the combination
    if (field === "recurrenceFrequency" || field === "recurrenceDurationUnit") {
      const appointment = (formData.treatmentPlan?.appointments || []).find(a => a.id === id);
      if (appointment) {
        const frequency = field === "recurrenceFrequency" ? value as string : appointment.recurrenceFrequency || 'Monthly';
        const durationUnit = field === "recurrenceDurationUnit" ? value as string : appointment.recurrenceDurationUnit || 'Months';
        
        const validation = isValidRecurrenceCombination(frequency, durationUnit);
        if (!validation.isValid) {
          toast({
            title: "Invalid Recurrence Combination",
            description: validation.message,
            variant: "destructive",
          });
          return; // Prevent the update
        }
      }
    }

    // If changing duration value, validate it's a positive number
    if (field === "recurrenceDurationValue") {
      const numValue = parseInt(value as string, 10);
      if (isNaN(numValue) || numValue < 1) {
        toast({
          title: "Invalid Duration",
          description: "Duration must be at least 1.",
          variant: "destructive",
        });
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        appointments: (prev.treatmentPlan?.appointments || []).map((a) =>
          a.id === id ? { ...a, [field]: value } : a,
        ),
      },
    }));
  },
  [formData.treatmentPlan?.appointments, toast],
);

  const removeTreatmentAppointment = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        appointments: (prev.treatmentPlan?.appointments || []).filter(
          (a) => a.id !== id,
        ),
      },
    }));
    setAddingTaskForAppt((prev) => (prev === id ? null : prev));
    setCollapsedAppointments((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const addTaskToAppointment = useCallback((apptId: string) => {
    if (!newTaskText.trim()) return;
    const newTask: TreatmentPlanTask = {
      id: `task_${Date.now()}`,
      text: newTaskText.trim(),
    };
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        appointments: (prev.treatmentPlan?.appointments || []).map((a) =>
          a.id === apptId ? { ...a, tasks: [...a.tasks, newTask] } : a,
        ),
      },
    }));
    setNewTaskText("");
  }, [newTaskText]);

  const removeTaskFromAppointment = useCallback((apptId: string, taskId: string) => {
    setFormData((prev) => ({
      ...prev,
      treatmentPlan: {
        appointments: (prev.treatmentPlan?.appointments || []).map((a) =>
          a.id === apptId
            ? { ...a, tasks: a.tasks.filter((t) => t.id !== taskId) }
            : a,
        ),
      },
    }));
  }, []);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm<ConsultationTemplate>({
    defaultValues: {
      ...formData,
      tempPrompt: templatePrompt || "",
    },
  });
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  useEffect(() => {
    if (generatedTemplates?.name) {
      reset({ name: generatedTemplates.name });
    }
  }, [generatedTemplates, reset]);

  useEffect(() => {
    if (!isStreaming && consultationStreamedSummary) {
      setGeneratedTemplates((prev) => {
        if (!prev) return prev;
        if (prev.consultationSummary === consultationStreamedSummary)
          return prev;
        return {
          ...prev,
          consultationSummary: consultationStreamedSummary,
        };
      });
    }
  }, [isStreaming, consultationStreamedSummary]);

  // Expands only appointments missing a name so the error is visible.
  const validateAppointmentNames = () => {
    const appointments = formData.treatmentPlan?.appointments || [];
    const invalidIds = appointments
      .filter((a) => !a.name.trim())
      .map((a) => a.id);
    if (invalidIds.length === 0) return true;

    setInvalidAppointmentIds(new Set(invalidIds));
    setCollapsedAppointments(
      new Set(appointments.filter((a) => a.name.trim()).map((a) => a.id)),
    );
    setDetailsSubTab("treatment");
    return false;
  };

  // Shows inline errors for blank section titles, sections without questions
  // and blank questions, and opens the affected sections.
  const validateSectionsForSave = () => {
    const { errors: nextErrors, sectionIdsWithErrors } =
      validateTemplateSections(formData.sections);

    setSectionFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length === 0) return true;

    setExpandedSections(
      (prev) => new Set([...Array.from(prev), ...sectionIdsWithErrors]),
    );
    setDetailsSubTab("questions");
    return false;
  };

  const onSubmit = async (data: any, e?: React.BaseSyntheticEvent) => {
    const submitEvent = e?.nativeEvent as SubmitEvent;
    const submitter = submitEvent?.submitter as HTMLButtonElement | undefined;
    const action = submitter?.value;

    const formDataWithFields = {
      ...data,
      linkedDocumentId: formData.linkedDocumentId || undefined,
      forceUserSignOff: formData.forceUserSignOff || false,
      lastReviewedAt: formData.lastReviewedAt,
      reviewNote: formData.reviewNote,
      sections: formData.sections,
      consultationSummary: formData.consultationSummary || "",
      consultationActions: (formData.consultationActions || []).map((a) => ({
        ...a,
        enabled: a.enabled === undefined ? false : a.enabled,
      })),
      treatmentPlan: formData.treatmentPlan,
    };

    if (action === "save") {
      if (!validateSectionsForSave()) return;
      if (!validateAppointmentNames()) return;
      await handleTemplateSubmit(formDataWithFields, onSave);
      setFormData((prev) => ({
        ...prev,
        reviewNote: "",
      }));
    }
    if (action === "generate") {
      await handleGenerateFromPrompt(formDataWithFields.tempPrompt || "");

      trackFirstTimeIntercomEvent({
        eventName: "consultation_template_created_first_time",
        user: {
          id: currentUser.emailAddress,
          email: currentUser.emailAddress,
          name: currentUser.fullName,
        },
        properties: {
          source: "consultation_settings",
        },
      });
    }
  };

  const hasLinkedDocumentSelected = Boolean(
    (formData as any)?.linkedDocumentId,
  );

  const handleReviewClick = useCallback(() => {
    if (!template || !hasLinkedDocumentSelected) return;
    setShowReviewModal(true);
  }, [template, hasLinkedDocumentSelected]);

  const generatedNotes = formData?.consultationSummary || "";
  const hasHeading = hasRequiredHeading(generatedNotes);

  return (
    <form onSubmit={handleFormSubmit(onSubmit)}>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div
          className={`sticky top-0 z-10 flex items-center gap-3 border-b pb-4 transition-colors duration-300 ${
            isDarkMode
              ? "bg-gray-900 border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className={
              isDarkMode
                ? "text-gray-300 hover:text-white hover:bg-gray-700"
                : ""
            }
          >
            ← Back
          </Button>
          <h2
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {template ? "Edit Template" : "Add New Template"}
          </h2>
        </div>

        <div key="details-tab" className="space-y-6">
          {/* Template Basic Info - Compressed */}
          {showBuilder && (
            <div
              className={`rounded-lg border px-4 py-3 transition-colors duration-300 ${
                isDarkMode
                  ? "border-gray-700 bg-gray-800"
                  : "border-gray-200 bg-white"
              }`}
            >
              <h3
                className={`text-base font-semibold mb-3 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Template Information
              </h3>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <Label
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Template Name *
                  </Label>
                  <div className="space-y-2">
                    <Input
                      {...register("name", {
                        required: "Template name is required",
                        minLength: { value: 1, message: "Too short" },
                      })}
                      placeholder="Enter template name"
                      maxLength={CONSULTATION_TEMPLATE_LIMITS.templateName}
                    />
                    <CharLimitMessage
                      value={watch("name")}
                      max={CONSULTATION_TEMPLATE_LIMITS.templateName}
                    />
                    {errors.name && (
                      <p className="text-sm text-red-500">
                        {errors.name.message}
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Label
                    className={`text-sm transition-colors duration-300 ${
                      isDarkMode ? "text-gray-200" : "text-gray-900"
                    }`}
                  >
                    Category
                  </Label>
                  <Select
                    value={formData?.category}
                    onValueChange={(value) => updateField("category", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      {/* <SelectItem value="specialty">Specialty</SelectItem>
                    <SelectItem value="follow-up">Follow-up</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem> */}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-3 md:col-span-3">
                  <div className="space-y-1">
                    <Label className="text-sm">Linked Document</Label>
                    <LinkedDocumentSelector
                      value={(formData as any).linkedDocumentId}
                      onChange={(id) =>
                        updateField("linkedDocumentId", id as any)
                      }
                    />
                  </div>
                  <div className="flex items-center justify-between px-3 py-2 border border-gray-200 rounded-md">
                    <div className="space-y-0.5">
                      <Label
                        className={`text-sm ${
                          !(formData as any)?.linkedDocumentId
                            ? "text-gray-400"
                            : ""
                        }`}
                      >
                        Force User Sign Off
                      </Label>
                      <p
                        className={`text-xs ${
                          !(formData as any)?.linkedDocumentId
                            ? "text-gray-300"
                            : "text-gray-500"
                        }`}
                      >
                        Require users to explicitly sign off on the linked
                        document before they can use this consultation template
                      </p>
                    </div>
                    <Switch
                      checked={(formData as any).forceUserSignOff || false}
                      onCheckedChange={(checked) =>
                        updateField("forceUserSignOff", checked as any)
                      }
                      disabled={!(formData as any).linkedDocumentId}
                    />
                  </div>
                </div>
                {/* Template Status and Review Info - Only show for existing templates */}
                {template && (
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <div
                        className={`text-xs font-medium transition-colors duration-300 ${
                          isDarkMode ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        Status
                      </div>
                      <div className="flex items-center gap-1">
                        <div
                          className={`w-1.5 h-1.5 rounded-full ${
                            template.isActive ? "bg-green-500" : "bg-gray-400"
                          }`}
                        />
                        <span
                          className={`text-xs transition-colors duration-300 ${
                            isDarkMode ? "text-gray-400" : "text-gray-600"
                          }`}
                        >
                          {template.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                    <Button
                      onClick={handleReviewClick}
                      disabled={!hasLinkedDocumentSelected}
                      variant="outline"
                      size="sm"
                      className={`px-2 text-xs text-green-600 border-green-600 h-7 hover:bg-green-50 hover:text-green-700 ${!hasLinkedDocumentSelected ? "!opacity-50 !cursor-not-allowed" : ""}`}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Review
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {!showBuilder ? (
          // AI Template Generation Interface
          <div className="space-y-6">
            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Create Consultation Template with AI
              </h4>
              <p
                className={`text-sm mb-6 transition-colors duration-300 ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Describe what kind of consultation template you need and
                optionally attach relevant files to help generate a customized
                template.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label
                  className={`transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Describe your consultation template
                </Label>
                <Textarea
                  // value={templatePrompt}
                  // onChange={(e) => setTemplatePrompt(e.target.value)}
                  {...register("tempPrompt", {
                    required: "Template prompt is required",
                    minLength: { value: 1, message: "Too short" },
                    maxLength: {
                      value: 5000,
                      message: "Prompt must be 5000 characters or less",
                    },
                  })}
                  maxLength={CONSULTATION_TEMPLATE_LIMITS.generationPrompt}
                  placeholder="e.g., 'Create a dermatology consultation template for skin condition assessments' or 'Generate questions for a general health consultation including symptoms and medical history'"
                  rows={4}
                />
                <CharLimitMessage
                  value={watch("tempPrompt")}
                  max={CONSULTATION_TEMPLATE_LIMITS.generationPrompt}
                />
                {errors.tempPrompt && (
                  <p className="text-sm text-red-500">
                    {errors.tempPrompt.message}
                  </p>
                )}
              </div>

              {/* Optional File Upload */}
              <div className="space-y-2">
                <Label
                  className={`transition-colors duration-300 ${
                    isDarkMode ? "text-gray-200" : "text-gray-900"
                  }`}
                >
                  Attach files for additional context (Optional)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="file"
                    onChange={handleDocumentUpload}
                    accept=".txt,.pdf,.doc,.docx"
                    className="hidden"
                    id="document-upload"
                  />
                  <Label
                    htmlFor="document-upload"
                    className="inline-flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-gray-50"
                  >
                    <Paperclip className="w-4 h-4" />
                    Attach File
                  </Label>
                  {uploadedDocument && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-500" />
                      <span
                        className={`text-sm ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        {uploadedDocument.name} (
                        {(uploadedDocument.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setUploadedDocument(null);
                          setDocumentContent("");
                          setFileError("");
                          // Clear the file input
                          const fileInput = document.getElementById(
                            "document-upload",
                          ) as HTMLInputElement;
                          if (fileInput) fileInput.value = "";
                        }}
                        className="h-auto p-1 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                {fileError && (
                  <p className="text-sm text-red-500">{fileError}</p>
                )}
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-gray-500" : "text-gray-500"
                  }`}
                >
                  {`Upload relevant medical documents, guidelines, or references
                  to enhance template generation. Maximum file size: ${documentSizeLimitInMbs || 5}MB.`}
                  <br />
                  Allowed file types: .txt, .pdf, .doc, .docx
                </p>
              </div>

              <Button
                type="submit"
                name="action"
                value="generate"
                className="w-full bg-slate-900 hover:bg-slate-800"
              >
                {isStreaming ? (
                  <>
                    <Clock className="w-4 h-4 mr-2 animate-spin" />
                    Generating Template...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Template
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          // Template Builder Interface - Consultation Page Style
          <div className="space-y-6">
            {/* Sub-tab Navigation for Notes, Questions, Actions */}
            <div
              className={`border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
            >
              <nav className="flex -mb-px space-x-6">
                <button
                  onClick={() => setDetailsSubTab("notes")}
                  className={`whitespace-nowrap py-2 px-1 !border-b font-medium text-sm transition-colors duration-150 ${
                    detailsSubTab === "notes"
                      ? isDarkMode
                        ? "border-white text-white"
                        : "border-slate-900 text-slate-900"
                      : isDarkMode
                        ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Notes Template
                  </div>
                </button>
                <button
                  onClick={() => setDetailsSubTab("questions")}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                    detailsSubTab === "questions"
                      ? isDarkMode
                        ? "border-white text-white"
                        : "border-slate-900 text-slate-900"
                      : isDarkMode
                        ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Edit2 className="w-4 h-4" />
                    Structured Questions
                  </div>
                </button>
                <button
                  onClick={() => setDetailsSubTab("actions")}
                  className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                    detailsSubTab === "actions"
                      ? isDarkMode
                        ? "border-white text-white"
                        : "border-slate-900 text-slate-900"
                      : isDarkMode
                        ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Consultation Actions
                  </div>
                </button>
               <button
                onClick={() => setDetailsSubTab("treatment")}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-150 ${
                  detailsSubTab === "treatment"
                    ? isDarkMode
                      ? "border-white text-white"
                      : "border-slate-900 text-slate-900"
                    : isDarkMode
                      ? "border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Treatment Plan
                </div>
              </button>
              </nav>
            </div>

            {/* Notes Template Tab */}
            {detailsSubTab === "notes" && (
              <div
                className={`rounded-lg border transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="px-4 py-3">
                  <h3
                    className={`text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Generated Notes Template
                  </h3>
                  <p
                    className={`text-sm mt-0.5 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Edit the consultation notes template as needed
                  </p>
                </div>

                <div className="px-4 pb-4">
                  <Textarea
                    {...register("consultationSummary", {
                      required: "Consultation Summary is required",
                    })}
                    value={formData?.consultationSummary || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      updateField("consultationSummary", val);
                      setValue("consultationSummary", val, {
                        shouldValidate: true,
                        shouldDirty: true,
                      });
                    }}
                    className={`w-full resize-none font-mono text-sm border-0 focus:ring-0 transition-colors duration-300 min-h-[500px] ${
                      isDarkMode
                        ? "bg-gray-900 text-gray-200"
                        : "bg-white text-gray-900"
                    }`}
                    placeholder="Consultation notes template..."
                    maxLength={CONSULTATION_TEMPLATE_LIMITS.consultationSummary}
                  />
                  <CharLimitMessage
                    value={formData?.consultationSummary}
                    max={CONSULTATION_TEMPLATE_LIMITS.consultationSummary}
                  />

                  {errors.consultationSummary && (
                    <p className="text-sm text-red-500">
                      {errors.consultationSummary.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Structured Questions Tab */}
            {detailsSubTab === "questions" && (
              <div
                className={`rounded-lg border transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <h3
                      className={`text-base font-semibold transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Structured Questions
                    </h3>
                    <p
                      className={`text-sm mt-0.5 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Questions grouped by consultation sections
                    </p>
                  </div>
                  <span
                    onClick={addSection}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md cursor-pointer bg-slate-900 hover:bg-slate-800"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Section
                  </span>
                </div>

                <div className="px-4 pb-4 space-y-4 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400">
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event: DragEndEvent) => {
                      const { active, over } = event;
                      if (over && active.id !== over.id) {
                        const oldIndex = formData.sections.findIndex(
                          (s) => s.id === active.id,
                        );
                        const newIndex = formData.sections.findIndex(
                          (s) => s.id === over.id,
                        );
                        reorderSections(oldIndex, newIndex);
                      }
                    }}
                  >
                    <SortableContext
                      items={formData.sections.map((s) => s.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {formData?.sections?.map((section, sectionIndex) => (
                        <SortableSectionItem
                          key={section.id}
                          section={section}
                          sectionIndex={sectionIndex}
                          isExpanded={expandedSections.has(section.id)}
                          isDarkMode={isDarkMode}
                          formData={formData}
                          errors={errors}
                          fieldErrors={sectionFieldErrors}
                          confirmDelete={confirmDelete}
                          updateSection={updateSection}
                          removeSection={removeSection}
                          toggleSection={toggleSection}
                          addQuestion={addQuestion}
                          updateQuestion={updateQuestion}
                          removeQuestion={removeQuestion}
                          reorderQuestions={reorderQuestions}
                          setValue={setValue}
                          reset={reset}
                          template={template}
                          templatePrompt={templatePrompt}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>

                  {formData?.sections.length === 0 && (
                    <div
                      className={`text-center py-8 border-2 border-dashed rounded-lg transition-colors duration-300 ${
                        isDarkMode ? "border-gray-600" : "border-gray-300"
                      }`}
                    >
                      <MessageSquare
                        className={`w-8 h-8 mx-auto mb-2 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      />
                      <p
                        className={`text-sm transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        No questions added yet. Click "Add Section" to create
                        your first section.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Consultation Actions Tab */}
            {detailsSubTab === "actions" && (
              <div
                className={`rounded-lg border transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="px-4 py-3">
                  <h3
                    className={`text-base font-semibold transition-colors duration-300 ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`}
                  >
                    Consultation Actions
                  </h3>
                  <p
                    className={`text-sm mt-0.5 transition-colors duration-300 ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`}
                  >
                    Define actions available during the consultation
                  </p>
                </div>
                <div className="px-4 pb-4 space-y-3">
                  {/* Add new action input */}
                  <div className="flex gap-2">
                    <Input
                      value={newActionLabel}
                      onChange={(e) => setNewActionLabel(e.target.value)}
                      maxLength={CONSULTATION_TEMPLATE_LIMITS.actionLabel}
                      placeholder="Enter action name (e.g. Blood Pressure Check, BMI Measurement)..."
                      className={`flex-1 h-9 text-sm ${
                        isDarkMode
                          ? "bg-gray-700 border-gray-600 text-white"
                          : ""
                      }`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addAction();
                        }
                      }}
                    />
                    <Button
                      onClick={addAction}
                      size="sm"
                      disabled={!newActionLabel.trim()}
                      className="bg-slate-900 hover:bg-slate-800 h-9"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  </div>
                  <CharLimitMessage
                    value={newActionLabel}
                    max={CONSULTATION_TEMPLATE_LIMITS.actionLabel}
                  />

                  {/* Actions list */}
                  {(formData.consultationActions || []).length > 0 ? (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleActionDragEnd}
                    >
                      <SortableContext
                        items={(formData.consultationActions || []).map(
                          (a) => a.id,
                        )}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-2">
                          {(formData.consultationActions || []).map(
                            (action, index) => (
                              <SortableActionItem
                                key={action.id}
                                action={action}
                                index={index}
                                isDarkMode={isDarkMode}
                                editingActionId={editingActionId}
                                editingActionLabel={editingActionLabel}
                                setEditingActionLabel={setEditingActionLabel}
                                saveEditAction={saveEditAction}
                                cancelEditAction={cancelEditAction}
                                startEditAction={startEditAction}
                                removeAction={removeAction}
                              />
                            ),
                          )}
                        </div>
                      </SortableContext>
                    </DndContext>
                  ) : (
                    <div className="py-8 text-center">
                      <ClipboardList
                        className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      />
                      <h4
                        className={`text-sm font-medium mb-1 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        No actions defined
                      </h4>
                      <p className="text-xs text-gray-500">
                        Add actions that will be available during consultations
                        using this template
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

             {/* Treatment Plan Tab */}
          {detailsSubTab === "treatment" && (
            <div className="space-y-6">
              {/* Appointments */}
              <div
                className={`rounded-lg border transition-colors duration-300 ${
                  isDarkMode
                    ? "border-gray-700 bg-gray-800"
                    : "border-gray-200 bg-white"
                }`}
              >
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <h3
                      className={`text-base font-semibold transition-colors duration-300 ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Appointments
                    </h3>
                    <p
                      className={`text-sm mt-0.5 transition-colors duration-300 ${
                        isDarkMode ? "text-gray-400" : "text-gray-600"
                      }`}
                    >
                      Appointments to schedule as part of this treatment plan
                    </p>
                  </div>
                  <span
                    onClick={addTreatmentAppointment}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium text-white rounded-md cursor-pointer bg-slate-900 hover:bg-slate-800"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add Appointment
                  </span>
                </div>
                <div className="px-4 pb-4 space-y-3">
                  {isPharmacyServiceLoading ? (
                    (formData.treatmentPlan?.appointments?.length
                      ? formData.treatmentPlan.appointments
                      : [{ id: "sk1" }]
                    ).map((_, i) => (
                      <div
                        key={i}
                        className={`rounded-lg border p-3 ${
                          isDarkMode
                            ? "border-gray-600 bg-gray-700/50"
                            : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-3">
                          <Skeleton variant="circular" width={16} height={16} />
                          <Skeleton variant="text" width={130} height={16} />
                        </div>
                        <Skeleton variant="rounded" height={32} className="mb-3" />
                        <div className="grid grid-cols-2 gap-3">
                          <Skeleton variant="rounded" height={32} />
                          <Skeleton variant="rounded" height={32} />
                          <Skeleton variant="rounded" height={32} />
                          <Skeleton variant="rounded" height={32} />
                        </div>
                      </div>
                    ))
                  ) : (formData.treatmentPlan?.appointments || []).length > 0 ? (
                    (formData.treatmentPlan?.appointments || []).map(
                      (appointment, index) => (
                        <div
                          key={appointment.id}
                          className={`rounded-lg border p-3 transition-colors duration-200 ${
                            isDarkMode
                              ? "border-gray-600 bg-gray-700/50"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <button
                              type="button"
                              onClick={() =>
                                toggleAppointmentCollapse(appointment.id)
                              }
                              className="flex items-center flex-1 min-w-0 gap-2 text-left"
                            >
                              <ChevronDown
                                className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${
                                  collapsedAppointments.has(appointment.id)
                                    ? "-rotate-90"
                                    : ""
                                } ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                              />
                              <span
                                className={`text-xs font-semibold uppercase tracking-wide flex-shrink-0 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                Appointment {index + 1}
                              </span>
                              {appointment.name.trim() && (
                                <span
                                  className={`text-sm font-medium truncate ${
                                    isDarkMode ? "text-gray-200" : "text-gray-800"
                                  }`}
                                >
                                  · {appointment.name}
                                </span>
                              )}
                            </button>
                            <Button
                              onClick={() =>
                                removeTreatmentAppointment(appointment.id)
                              }
                              size="sm"
                              variant="ghost"
                              className="flex-shrink-0 p-0 text-red-400 h-7 w-7 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {!collapsedAppointments.has(appointment.id) && (
                          <>
                          {/* Appointment name */}
                          <div className="mt-3 mb-3 space-y-1">
                            <Label
                              className={`text-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Appointment Name *
                            </Label>
                            <Input
                              value={appointment.name}
                              onChange={(e) =>
                                updateTreatmentAppointment(
                                  appointment.id,
                                  "name",
                                  e.target.value,
                                )
                              }
                              placeholder="e.g. Initial Assessment, Follow-up Review..."
                              maxLength={CONSULTATION_TEMPLATE_LIMITS.appointmentName}
                              className={`h-8 text-sm ${
                                isDarkMode
                                  ? "bg-gray-700 border-gray-600 text-white"
                                  : ""
                              } ${
                                invalidAppointmentIds.has(appointment.id)
                                  ? "border-red-500"
                                  : ""
                              }`}
                            />
                            <CharLimitMessage
                              value={appointment.name}
                              max={CONSULTATION_TEMPLATE_LIMITS.appointmentName}
                            />
                            {invalidAppointmentIds.has(appointment.id) && (
                              <p className="text-sm text-red-500">
                                Appointment name is required
                              </p>
                            )}
                          </div>
                          {/* Interval from previous appointment — not shown for the first appointment */}
                          {index > 0 && (
                          <div className="mb-3 space-y-1">
                            <Label
                              className={`text-xs ${
                                isDarkMode ? "text-gray-300" : "text-gray-700"
                              }`}
                            >
                              Schedule after previous appointment
                            </Label>
                            <div className="flex gap-2">
                              <Input
                                type="number"
                                min="0"
                                value={appointment.intervalValue ?? ""}
                                onChange={(e) =>
                                  updateTreatmentAppointment(
                                    appointment.id,
                                    "intervalValue",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g. 2"
                                className={`h-8 text-sm w-24 ${
                                  isDarkMode
                                    ? "bg-gray-700 border-gray-600 text-white"
                                    : ""
                                }`}
                              />
                              <Select
                                value={appointment.intervalUnit ?? "Weeks"}
                                onValueChange={(value) =>
                                  updateTreatmentAppointment(
                                    appointment.id,
                                    "intervalUnit",
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm flex-1 ${
                                    isDarkMode
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : ""
                                  }`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Days">Days</SelectItem>
                                  <SelectItem value="Weeks">Weeks</SelectItem>
                                  <SelectItem value="Months">Months</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <p
                              className={`text-[11px] ${
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }`}
                            >
                              How far apart from the previous appointment this one should be scheduled.
                            </p>
                          </div>
                          )}
                          {/* Recurring appointment */}
                          <div className="mb-3 space-y-2">
  <div className="flex items-center gap-2">
    <Label
      className={`text-xs ${
        isDarkMode ? "text-gray-300" : "text-gray-700"
      }`}
    >
      Repeat this appointment
    </Label>
    <Switch
      checked={appointment.isRecurring ?? false}
      onCheckedChange={(checked) =>
        updateTreatmentAppointment(
          appointment.id,
          "isRecurring",
          checked,
        )
      }
    />
  </div>
  {appointment.isRecurring && (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <span
          className={`text-xs ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          Repeat
        </span>
        <Select
          value={appointment.recurrenceFrequency ?? "Monthly"}
          onValueChange={(value) =>
            updateTreatmentAppointment(
              appointment.id,
              "recurrenceFrequency",
              value,
            )
          }
        >
          <SelectTrigger
            className={`h-8 text-sm w-32 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white"
                : ""
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Weekly">Weekly</SelectItem>
            <SelectItem value="Monthly">Monthly</SelectItem>
            <SelectItem value="Yearly">Yearly</SelectItem>
          </SelectContent>
        </Select>
        <span
          className={`text-xs ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
        >
          for
        </span>
        <Input
          type="number"
          min="1"
          value={appointment.recurrenceDurationValue ?? ""}
          onChange={(e) => {
            const val = e.target.value;
            if (val === "" || parseInt(val, 10) >= 1) {
              updateTreatmentAppointment(
                appointment.id,
                "recurrenceDurationValue",
                val,
              );
            }
          }}
          placeholder="e.g. 6"
          className={`h-8 text-sm w-20 ${
            isDarkMode
              ? "bg-gray-700 border-gray-600 text-white"
              : ""
          }`}
        />
        <Select
          value={appointment.recurrenceDurationUnit ?? "Months"}
          onValueChange={(value) =>
            updateTreatmentAppointment(
              appointment.id,
              "recurrenceDurationUnit",
              value,
            )
          }
        >
          <SelectTrigger
            className={`h-8 text-sm w-28 ${
              isDarkMode
                ? "bg-gray-700 border-gray-600 text-white"
                : ""
            }`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {/* Show only valid combinations based on frequency */}
            {(() => {
              const frequency = appointment.recurrenceFrequency ?? "Monthly";
              const validUnits: Record<string, string[]> = {
                'Weekly': ['Weeks', 'Months'],
                'Monthly': ['Months', 'Years'],
                'Yearly': ['Months', 'Years']
              };
              const availableUnits = validUnits[frequency] || ['Months'];
              return availableUnits.map(unit => (
                <SelectItem key={unit} value={unit}>
                  {unit}
                </SelectItem>
              ));
            })()}
          </SelectContent>
        </Select>
      </div>
      
      {/* Show helpful description with the actual combination */}
      <p
        className={`text-[11px] ${
          isDarkMode ? "text-gray-500" : "text-gray-400"
        }`}
      >
        Repeats {appointment.recurrenceFrequency ?? "Monthly"}
        {appointment.recurrenceDurationValue
          ? ` for ${appointment.recurrenceDurationValue} ${appointment.recurrenceDurationUnit ?? "Months"}`
          : ""}
        {isValidRecurrenceCombination(
          appointment.recurrenceFrequency ?? "Monthly",
          appointment.recurrenceDurationUnit ?? "Months"
        ).isValid ? (
          <span className="ml-1 text-green-500">✓</span>
        ) : (
          <span className="ml-1 text-red-500">
            ⚠️ Invalid combination - please adjust
          </span>
        )}
      </p>
    </>
  )}
</div>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div className="space-y-1">
                              <Label
                                className={`text-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Service Type
                              </Label>
                              <Select
                                value={appointment.serviceId}
                                onValueChange={async (value) => {
                                  updateTreatmentAppointment(
                                    appointment.id,
                                    "serviceId",
                                    value,
                                  );
                                  const svc = mappedServices.find((s) => s._id === value);
                                  const base = parseInt(String(svc?.serviceDuration ?? 0), 10) || 0;
                                  if (base > 0) {
                                    updateTreatmentAppointment(
                                      appointment.id,
                                      "duration",
                                      String(base),
                                    );
                                  }
                                  // clear room when service changes
                                  updateTreatmentAppointment(appointment.id, "roomId", "");
                                  updateTreatmentAppointment(appointment.id, "room", "");
                                  await fetchRoomsForService(value);
                                }}
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${
                                    isDarkMode
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select service type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {/* {TREATMENT_PLAN_SERVICE_TYPES.map((type) => (
                                    <SelectItem key={type} value={type}>
                                      {type}
                                    </SelectItem>
                                  ))} */}
                                  {mappedServices.map((svc) => (
                                    <SelectItem key={svc._id} value={svc._id}>
                                      {svc.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label
                                className={`text-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Category
                              </Label>
                              <Select
                                value={appointment.category}
                                onValueChange={(value) =>
                                  updateTreatmentAppointment(
                                    appointment.id,
                                    "category",
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${
                                    isDarkMode
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                  {TREATMENT_PLAN_CATEGORIES.map((cat) => (
                                    <SelectItem key={cat} value={cat}>
                                      {cat}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label
                                className={`text-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Duration
                              </Label>
                              <Select
                                value={appointment.duration}
                                onValueChange={(value) =>
                                  updateTreatmentAppointment(
                                    appointment.id,
                                    "duration",
                                    value,
                                  )
                                }
                              >
                                <SelectTrigger
                                  className={`h-8 text-sm ${
                                    isDarkMode
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : ""
                                  }`}
                                >
                                  <SelectValue placeholder="Select duration" />
                                </SelectTrigger>
                                <SelectContent>
                                  {getDurationsForService(appointment.serviceId, appointment.duration).map((d) => (
                                    <SelectItem key={d.value} value={d.value}>
                                      {d.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label
                                className={`text-xs ${
                                  isDarkMode ? "text-gray-300" : "text-gray-700"
                                }`}
                              >
                                Room
                              </Label>
                              {loadingRoomsServiceIds.has(appointment.serviceId) ? (
                                <Skeleton variant="rounded" height={32} />
                              ) : (
                                <Select
                                  value={appointment.roomId}
                                  onValueChange={(value) => {
                                    updateTreatmentAppointment(
                                      appointment.id,
                                      "roomId",
                                      value,
                                    );
                                    const room = (roomsByServiceId[appointment.serviceId] || []).find(
                                      (r: any) => r.roomId === value,
                                    );
                                    updateTreatmentAppointment(
                                      appointment.id,
                                      "room",
                                      room?.roomName || "",
                                    );
                                  }}
                                >
                                  <SelectTrigger
                                    className={`h-8 text-sm ${
                                      isDarkMode
                                        ? "bg-gray-700 border-gray-600 text-white"
                                        : ""
                                    }`}
                                  >
                                    <SelectValue placeholder="Select room" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {/* {TREATMENT_PLAN_ROOMS.map((room) => (
                                      <SelectItem key={room} value={room}>
                                        {room}
                                      </SelectItem>
                                    ))} */}
                                    {(roomsByServiceId[appointment.serviceId] || []).map((room: any) => (
                                      <SelectItem key={room.roomId} value={room.roomId}>
                                        {room.roomName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          </div>

                          {/* Pre-consultation form */}
                          <div className={`mt-3 pt-3 border-t border-dashed ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            {/* <div className="flex items-center gap-2 mb-2">
                              <span className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                <Mail className="w-3 h-3" />
                                Pre-Consultation Form2
                              </span>
                              <Switch
                                checked={appointment.sendPreConsultationForm ?? false}
                                onCheckedChange={(checked) => {
                                  updateTreatmentAppointment(appointment.id, "sendPreConsultationForm", checked);
                                  if (checked && companyId) {
                                    getPreConsultTemplateByCompanyId(companyId);
                                  }
                                }}
                              />
                            </div> */}
                            {appointment.sendPreConsultationForm && (
                              <div className="mt-2 space-y-2">
                                <div className="space-y-1">
                                  <Label className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                                    Template to send
                                  </Label>
                                  <Select
                                    value={appointment.preConsultationTemplateId ?? ""}
                                    onValueChange={(value) =>
                                      updateTreatmentAppointment(appointment.id, "preConsultationTemplateId", value)
                                    }
                                  >
                                    <SelectTrigger className={`h-8 text-sm ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}>
                                      <SelectValue placeholder="Select a form template" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {(currentPreConsultTemplates || []).map((t: any) => (
                                        <SelectItem key={t._id} value={t._id}>{t.templateName}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div className="space-y-1">
                                  <Label className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
                                    Send this form
                                  </Label>
                                  <div className="flex items-center gap-2">
                                    <Input
                                      type="number"
                                      min="1"
                                      value={appointment.preConsultationSendValue ?? "24"}
                                      onChange={(e) =>
                                        updateTreatmentAppointment(appointment.id, "preConsultationSendValue", e.target.value)
                                      }
                                      className={`h-8 text-sm w-20 ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}
                                    />
                                    <Select
                                      value={appointment.preConsultationSendUnit ?? "Hours"}
                                      onValueChange={(value) =>
                                        updateTreatmentAppointment(appointment.id, "preConsultationSendUnit", value)
                                      }
                                    >
                                      <SelectTrigger className={`h-8 text-sm w-32 ${isDarkMode ? "bg-gray-700 border-gray-600 text-white" : ""}`}>
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Hours">Hours before</SelectItem>
                                        <SelectItem value="Days">Days before</SelectItem>
                                      </SelectContent>
                                    </Select>
                                    <span className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                                      appointment
                                    </span>
                                  </div>
                                  {appointment.preConsultationSendValue && (
                                    <p className={`text-[11px] ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
                                      Form sent {appointment.preConsultationSendValue} {appointment.preConsultationSendUnit ?? "Hours"} before the appointment.
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Tasks within this appointment */}
                          <div className={`mt-3 pt-3 border-t border-dashed ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span
                                className={`text-xs font-semibold uppercase tracking-wide flex items-center gap-1 ${
                                  isDarkMode ? "text-gray-400" : "text-gray-500"
                                }`}
                              >
                                <ClipboardList className="w-3 h-3" />
                                Tasks
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setAddingTaskForAppt((prev) =>
                                    prev === appointment.id
                                      ? null
                                      : appointment.id,
                                  )
                                }
                                className={`text-xs flex items-center gap-1 px-2 py-0.5 rounded transition-colors ${
                                  isDarkMode
                                    ? "text-gray-400 hover:text-white hover:bg-gray-600"
                                    : "text-gray-500 hover:text-gray-800 hover:bg-gray-200"
                                }`}
                              >
                                <Plus className="w-3 h-3" />
                                Add task
                              </button>
                            </div>

                            {/* Existing tasks */}
                            {appointment.tasks.length > 0 && (
                              <div className="space-y-1.5 mb-2">
                                {appointment.tasks.map((task, ti) => (
                                  <div
                                    key={task.id}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded border text-xs ${
                                      isDarkMode
                                        ? "border-gray-600 bg-gray-800/60 text-gray-300"
                                        : "border-gray-200 bg-white text-gray-700"
                                    }`}
                                  >
                                    <span
                                      className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 text-[10px] font-medium ${
                                        isDarkMode
                                          ? "bg-gray-600 text-gray-300"
                                          : "bg-gray-100 text-gray-500"
                                      }`}
                                    >
                                      {ti + 1}
                                    </span>
                                    <span className="flex-1">{task.text}</span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        removeTaskFromAppointment(
                                          appointment.id,
                                          task.id,
                                        )
                                      }
                                      className="flex-shrink-0 text-red-400 hover:text-red-600"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Inline task input for this appointment */}
                            {addingTaskForAppt === appointment.id && (
                              <div className="flex gap-2 mt-1">
                                <Input
                                  value={newTaskText}
                                  onChange={(e) => setNewTaskText(e.target.value)}
                                  maxLength={CONSULTATION_TEMPLATE_LIMITS.taskText}
                                  placeholder="e.g. Send referral letter..."
                                  className={`flex-1 h-7 text-xs ${
                                    isDarkMode
                                      ? "bg-gray-700 border-gray-600 text-white"
                                      : ""
                                  }`}
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      e.preventDefault();
                                      addTaskToAppointment(appointment.id);
                                    }
                                    if (e.key === "Escape") {
                                      setAddingTaskForAppt(null);
                                      setNewTaskText("");
                                    }
                                  }}
                                />
                                <Button
                                  onClick={() =>
                                    addTaskToAppointment(appointment.id)
                                  }
                                  size="sm"
                                  disabled={!newTaskText.trim()}
                                  className="px-2 h-7 bg-slate-900 hover:bg-slate-800"
                                >
                                  <Plus className="w-3 h-3" />
                                </Button>
                                <Button
                                  onClick={() => {
                                    setAddingTaskForAppt(null);
                                    setNewTaskText("");
                                  }}
                                  size="sm"
                                  variant="ghost"
                                  className="px-2 text-gray-400 h-7"
                                >
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            )}
                            {addingTaskForAppt === appointment.id && (
                              <CharLimitMessage
                                value={newTaskText}
                                max={CONSULTATION_TEMPLATE_LIMITS.taskText}
                                className="text-xs"
                              />
                            )}

                            {appointment.tasks.length === 0 &&
                              addingTaskForAppt !== appointment.id && (
                                <p
                                  className={`text-xs italic ${
                                    isDarkMode ? "text-gray-500" : "text-gray-400"
                                  }`}
                                >
                                  No tasks — click "Add task" above to add one
                                </p>
                              )}
                          </div>
                          </>
                          )}
                        </div>
                      ),
                    )
                  ) : (
                    <div className="py-8 text-center">
                      <Calendar
                        className={`w-10 h-10 mx-auto mb-3 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-600" : "text-gray-400"
                        }`}
                      />
                      <h4
                        className={`text-sm font-medium mb-1 transition-colors duration-300 ${
                          isDarkMode ? "text-gray-400" : "text-gray-600"
                        }`}
                      >
                        No appointments defined
                      </h4>
                      <p className="text-xs text-gray-500">
                        Add the appointments required for this treatment plan
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

            {/* Settings */}
            <div>
              <h4
                className={`font-medium mb-4 transition-colors duration-300 ${
                  isDarkMode ? "text-white" : "text-gray-900"
                }`}
              >
                Template Settings
              </h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={formData?.isActive}
                    onCheckedChange={(checked) =>
                      updateField("isActive", checked)
                    }
                  />
                  <Label>Active (available for use)</Label>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
          {showBuilder && !hasHeading && (
            <p className="mr-auto text-xs text-red-500">
              At least one heading (e.g. "Medical History" or "Presenting
              Complaint") must be present in Generated Notes Template to save.
            </p>
          )}
          <Button variant="outline" onClick={onBack}>
            Cancel
          </Button>
          {showBuilder && (
            <Button
              disabled={buttonDisabled || isStreaming || !hasHeading}
              type="submit"
              name="action"
              value="save"
              className="bg-slate-900 hover:bg-slate-800"
            >
              <Save className="w-4 h-4 mr-2" />
              {buttonDisabled ? "Processing..." : "Save Template"}
            </Button>
          )}
        </div>
        {/* Review Template Modal */}
        {showReviewModal && (
          <ReviewTemplateModal
            isOpen={showReviewModal}
            onClose={() => setShowReviewModal(false)}
            onReview={handleReviewSubmit}
            templateName={template?.name || "Consultation Template"}
            initialReviewNotes={formData.reviewNote || ""}
            isDarkMode={isDarkMode}
          />
        )}
      </div>
    </form>
  );
}

export function ConsultationPreviewModal({
  template,
  onClose,
}: ConsultationPreviewModalProps) {
  const { darkMode: isDarkMode } = useConsultationsData();
  return (
    <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Stethoscope className="w-5 h-5" />
            Template Preview: {template.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template Info */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-gray-50">
            <Badge variant="outline" className="text-xs">
              {template.category.toUpperCase()}
            </Badge>
            <span className="text-sm text-gray-600">
              This preview shows how the template will appear during
              consultations
            </span>
          </div>

          {/* Consultation Form Preview */}
          <div className="overflow-hidden overflow-y-auto border rounded-lg max-h-96">
            <div className="p-4 space-y-4 bg-white">
              <div className="pb-2 border-b">
                <h3 className="text-lg font-semibold">{template.name}</h3>
                <p className="text-sm text-gray-600">{template.description}</p>
              </div>

              {template.sections.map((section, index) => (
                <div
                  key={section.id}
                  className="overflow-hidden border rounded-lg"
                >
                  {/* Section Header */}
                  <div className="flex items-center justify-between p-4 bg-white">
                    <div className="flex items-center flex-1 gap-3">
                      <div className="flex items-center justify-center w-6 h-6 text-sm font-semibold text-white bg-black rounded-full">
                        {index + 1}
                      </div>
                      <h4 className="text-base font-medium text-gray-900">
                        {section.title}
                      </h4>
                    </div>
                  </div>

                  {/* Questions Content */}
                  <div className="p-4 space-y-3 border-t bg-gray-50">
                    {section.questions.map((question) => (
                      <div
                        key={question.id}
                        className="p-3 bg-gray-100 rounded-lg"
                      >
                        <div className="flex items-start gap-3">
                          <GripVertical className="flex-shrink-0 w-4 h-4 mt-1 text-gray-500" />
                          <div className="flex-1 space-y-2">
                            <div className="text-sm text-gray-900">
                              {question.question}
                              {question.required && (
                                <span className="ml-1 text-red-500">*</span>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <Switch checked={question.required} disabled />
                              <span className="text-sm text-gray-600">
                                Required
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {section.questions.length === 0 && (
                      <div className="py-4 text-center border-2 border-gray-300 border-dashed rounded-lg">
                        <p className="text-sm text-gray-600">
                          No questions in this section.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Template Stats */}
          <div className="grid grid-cols-3 gap-4 text-sm text-center">
            <div>
              <div className="font-semibold">{template.sections.length}</div>
              <div className="text-gray-500">Sections</div>
            </div>
            <div>
              <div className="font-semibold">{template.usageCount}</div>
              <div className="text-gray-500">Times Used</div>
            </div>
            <div>
              <div className="font-semibold">
                {template.sections.reduce(
                  (acc, section) =>
                    acc + section.questions.filter((q) => q.required).length,
                  0,
                )}
              </div>
              <div className="text-gray-500">Required Fields</div>
            </div>
          </div>

          {/* Close Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={onClose}
              className="bg-slate-900 hover:bg-slate-800"
            >
              Close Preview
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface SortableSectionItemProps {
  section: ConsultationSection;
  sectionIndex: number;
  isExpanded: boolean;
  isDarkMode: boolean;
  formData: ConsultationTemplate;
  errors: any;
  fieldErrors: Record<string, string>;
  confirmDelete: (fn: () => void, msg?: string) => void;
  updateSection: (
    index: number,
    field: keyof ConsultationSection,
    value: any,
  ) => void;
  removeSection: (index: number) => void;
  toggleSection: (id: string) => void;
  addQuestion: (index: number) => void;
  updateQuestion: (
    si: number,
    qi: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => void;
  removeQuestion: (si: number, qi: number) => void;
  reorderQuestions: (si: number, startIndex: number, endIndex: number) => void;
  setValue: any;
  reset: any;
  template: ConsultationTemplate | undefined;
  templatePrompt: string;
}

function SortableSectionItem({
  section,
  sectionIndex,
  isExpanded,
  isDarkMode,
  formData,
  errors,
  fieldErrors,
  confirmDelete,
  updateSection,
  removeSection,
  toggleSection,
  addQuestion,
  updateQuestion,
  removeQuestion,
  reorderQuestions,
  setValue,
  reset,
  template,
  templatePrompt,
}: SortableSectionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const questionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg overflow-hidden transition-colors duration-300 ${
        isDarkMode ? "border-gray-600" : "border-gray-200"
      }`}
    >
      {/* Section Header */}
      <div
        className={`flex items-center justify-between p-4 transition-colors duration-300 ${
          isDarkMode ? "bg-gray-800" : "bg-white"
        }`}
      >
        <div className="flex items-center flex-1 gap-3">
          {/* DRAG HANDLE */}
          <button
            {...attributes}
            {...listeners}
            className={`cursor-grab active:cursor-grabbing ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-300"
                : "text-gray-500 hover:text-gray-700"
            }`}
            type="button"
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <div
            className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-semibold ${
              isDarkMode ? "bg-gray-600 text-white" : "bg-black text-white"
            }`}
          >
            {sectionIndex + 1}
          </div>
          <Input
            value={section.title}
            onChange={(e) =>
              updateSection(sectionIndex, "title", e.target.value)
            }
            className={`font-medium text-base bg-transparent border-0 px-0 py-0 h-auto focus:ring-0 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
            placeholder="Section name..."
            maxLength={CONSULTATION_TEMPLATE_LIMITS.sectionTitle}
          />
          <CharLimitMessage
            value={section.title}
            max={CONSULTATION_TEMPLATE_LIMITS.sectionTitle}
            className="mt-0 whitespace-nowrap"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => addQuestion(sectionIndex)}
            className={
              isDarkMode
                ? "text-gray-400 hover:text-white p-1 h-auto"
                : "text-gray-500 hover:text-gray-700 p-1 h-auto"
            }
          >
            <Plus className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              confirmDelete(
                () => removeSection(sectionIndex),
                deleteConfirmationMessage + "section",
              )
            }
            className="h-auto p-1 text-red-500 hover:text-red-700"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            type="button"
            onClick={() => toggleSection(section.id)}
            className={
              isDarkMode
                ? "text-gray-400 hover:text-white p-1 h-auto"
                : "text-gray-500 hover:text-gray-700 p-1 h-auto"
            }
          >
            <ChevronDown
              className={`w-4 h-4 transition-all duration-300 ${isExpanded ? "rotate-180" : ""}`}
            />
          </Button>
        </div>
      </div>
      {(fieldErrors[`title-${sectionIndex}`] ||
        fieldErrors[`questions-${sectionIndex}`]) && (
        <div
          className={`px-4 pb-3 space-y-1 ${
            isDarkMode ? "bg-gray-800" : "bg-white"
          }`}
        >
          {fieldErrors[`title-${sectionIndex}`] && (
            <p className="text-sm text-red-500">
              {fieldErrors[`title-${sectionIndex}`]}
            </p>
          )}
          {fieldErrors[`questions-${sectionIndex}`] && (
            <p className="text-sm text-red-500">
              {fieldErrors[`questions-${sectionIndex}`]}
            </p>
          )}
        </div>
      )}

      {/* Questions — only when expanded */}
      {isExpanded && (
        <div
          className={`border-t p-4 space-y-3 transition-colors duration-300 ${
            isDarkMode
              ? "border-gray-600 bg-gray-900"
              : "border-gray-200 bg-gray-50"
          }`}
        >
          <DndContext
            sensors={questionSensors}
            collisionDetection={closestCenter}
            onDragEnd={(event: DragEndEvent) => {
              const { active, over } = event;
              if (over && active.id !== over.id) {
                const oldIndex = section.questions.findIndex(
                  (q) => q.id === active.id,
                );
                const newIndex = section.questions.findIndex(
                  (q) => q.id === over.id,
                );
                reorderQuestions(sectionIndex, oldIndex, newIndex);
              }
            }}
          >
            <SortableContext
              items={section.questions.map((q) => q.id)}
              strategy={verticalListSortingStrategy}
            >
              {section.questions.map((question, questionIndex) => (
                <SortableQuestionItem
                  key={question.id}
                  question={question}
                  questionIndex={questionIndex}
                  sectionIndex={sectionIndex}
                  isDarkMode={isDarkMode}
                  errors={errors}
                  fieldError={
                    fieldErrors[`question-${sectionIndex}-${questionIndex}`]
                  }
                  confirmDelete={confirmDelete}
                  updateQuestion={updateQuestion}
                  removeQuestion={removeQuestion}
                  setValue={setValue}
                  reset={reset}
                  formData={formData}
                  template={template}
                  templatePrompt={templatePrompt}
                />
              ))}
            </SortableContext>
          </DndContext>

          {section.questions.length === 0 && (
            <div
              className={`text-center py-4 border-2 border-dashed rounded-lg ${
                isDarkMode ? "border-gray-600" : "border-gray-300"
              }`}
            >
              <p
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                No questions yet. Click the + button to add a question.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface SortableQuestionItemProps {
  question: ConsultationQuestion;
  questionIndex: number;
  sectionIndex: number;
  isDarkMode: boolean;
  errors: any;
  fieldError?: string;
  confirmDelete: (fn: () => void, msg?: string) => void;
  updateQuestion: (
    si: number,
    qi: number,
    field: keyof ConsultationQuestion,
    value: any,
  ) => void;
  removeQuestion: (si: number, qi: number) => void;
  setValue: any;
  reset: any;
  formData: ConsultationTemplate;
  template: ConsultationTemplate | undefined;
  templatePrompt: string;
}

function SortableQuestionItem({
  question,
  questionIndex,
  sectionIndex,
  isDarkMode,
  errors,
  fieldError,
  confirmDelete,
  updateQuestion,
  removeQuestion,
  setValue,
  reset,
  formData,
  template,
  templatePrompt,
}: SortableQuestionItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg p-3 transition-colors duration-300 ${
        isDarkMode
          ? "bg-gray-700 hover:bg-gray-600"
          : "bg-gray-100 hover:bg-gray-200"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* DRAG HANDLE */}
        <button
          {...attributes}
          {...listeners}
          className={`cursor-grab active:cursor-grabbing flex-shrink-0 mt-1 ${
            isDarkMode
              ? "text-gray-400 hover:text-gray-300"
              : "text-gray-500 hover:text-gray-700"
          }`}
          type="button"
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 space-y-3">
          <Textarea
            value={question.question}
            onChange={(e) => {
              const val = e.target.value;
              updateQuestion(sectionIndex, questionIndex, "question", val);
              setValue(
                `sections.${sectionIndex}.questions.${questionIndex}.question`,
                val,
                {
                  shouldValidate: true,
                },
              );
              if (!template) {
                reset(
                  { ...formData, tempPrompt: templatePrompt || "" },
                  {
                    keepErrors: true,
                    keepDirty: false,
                  },
                );
              }
            }}
            placeholder="Enter your question..."
            rows={2}
            maxLength={CONSULTATION_TEMPLATE_LIMITS.questionLabel}
            className={`text-sm ${
              isDarkMode
                ? "bg-gray-800 border-gray-600 text-white"
                : "bg-white border-gray-300"
            } ${fieldError ? "border-red-500" : ""}`}
          />
          <CharLimitMessage
            value={question.question}
            max={CONSULTATION_TEMPLATE_LIMITS.questionLabel}
          />
          {fieldError && <p className="text-sm text-red-500">{fieldError}</p>}
          {errors.sections?.[sectionIndex]?.questions?.[questionIndex]
            ?.question && (
            <p className="text-sm text-red-500">
              {
                errors.sections[sectionIndex].questions[questionIndex].question
                  ?.message
              }
            </p>
          )}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Switch
                checked={question.required}
                onCheckedChange={(checked) =>
                  updateQuestion(
                    sectionIndex,
                    questionIndex,
                    "required",
                    checked,
                  )
                }
              />
              <span
                className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
              >
                Required
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                confirmDelete(
                  () => removeQuestion(sectionIndex, questionIndex),
                  deleteConfirmationMessage + "question",
                )
              }
              className="h-auto p-1 text-red-500 hover:text-red-700"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
